import { useState, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import SignatureCanvas from "react-signature-canvas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WaiverPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const sigCanvas = useRef(null);
  
  const memberId = location.state?.memberId || localStorage.getItem("memberData") ? JSON.parse(localStorage.getItem("memberData")).id : null;
  
  const [agreedToWaiver, setAgreedToWaiver] = useState(false);
  const [loading, setLoading] = useState(false);

  const waiverText = `
    LIABILITY WAIVER AND RELEASE FORM
    NEIGHBORHOOD BOXING CLUB
    
    I understand that participating in boxing training and fitness activities involves inherent risks, 
    including but not limited to physical injury, disability, and death. I voluntarily assume all risks 
    associated with my participation in any and all activities at Neighborhood Boxing Club.
    
    I release, waive, and discharge Neighborhood Boxing Club, its owners, trainers, employees, and agents 
    from any and all liability for any injury, damage, or loss that I may sustain as a result of participating 
    in any activities at the facility.
    
    I certify that I am physically fit and have no medical condition that would prevent my participation 
    in boxing training and fitness activities. I agree to follow all safety rules and instructions provided 
    by the staff.
    
    By signing below, I acknowledge that I have read this waiver, understand its contents, and agree to 
    its terms.
    
    This waiver shall be binding upon my heirs, executors, administrators, and assigns.
  `;

  const handleSubmit = async () => {
    if (!agreedToWaiver) {
      toast.error("Please agree to the waiver terms");
      return;
    }
    
    if (sigCanvas.current?.isEmpty()) {
      toast.error("Please sign the waiver");
      return;
    }

    if (!memberId) {
      toast.error("Please log in first");
      navigate("/login");
      return;
    }

    setLoading(true);
    try {
      const signatureData = sigCanvas.current.toDataURL();
      const token = localStorage.getItem("memberToken");
      
      await axios.post(
        `${API}/waivers`,
        {
          member_id: memberId,
          signature_data: signatureData
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Waiver signed successfully!");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to submit waiver");
    } finally {
      setLoading(false);
    }
  };

  const clearSignature = () => {
    sigCanvas.current?.clear();
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <img 
            src="/nbc_logo.jpg"
            alt="NBC"
            className="h-16 mx-auto mb-6"
          />
          <h1 className="text-3xl font-bold uppercase">Liability Waiver</h1>
          <p className="text-white/60 mt-2">Please read and sign the waiver to continue</p>
        </div>

        <div className="bg-[#0A0A0A] border border-white/10 p-8 space-y-6">
          {/* Waiver Text */}
          <div className="bg-[#050505] border border-white/10 p-6 max-h-64 overflow-y-auto">
            <pre className="text-sm text-white/70 whitespace-pre-wrap font-sans leading-relaxed">
              {waiverText}
            </pre>
          </div>

          {/* Agreement Checkbox */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreedToWaiver}
              onChange={(e) => setAgreedToWaiver(e.target.checked)}
              className="mt-1 w-5 h-5 accent-[#FFB612]"
              data-testid="waiver-agree-checkbox"
            />
            <span className="text-sm text-white/70">
              I have read, understand, and agree to the terms of the liability waiver above. 
              I acknowledge that I am voluntarily participating in boxing and fitness activities 
              and assume all associated risks.
            </span>
          </label>

          {/* Signature Pad */}
          <div>
            <p className="text-sm text-white/60 mb-3 font-medium">Sign Below:</p>
            <div className="bg-white border-2 border-[#FFB612] relative">
              <SignatureCanvas
                ref={sigCanvas}
                penColor="black"
                canvasProps={{
                  className: "w-full h-40",
                  style: { width: "100%", height: "160px" }
                }}
              />
              <div className="absolute bottom-2 left-2 right-2 border-t border-gray-300" />
            </div>
            <div className="flex justify-between items-center mt-2">
              <p className="text-xs text-white/40">Use your finger or mouse to sign</p>
              <Button
                type="button"
                variant="ghost"
                onClick={clearSignature}
                className="text-white/50 hover:text-white text-sm"
              >
                Clear Signature
              </Button>
            </div>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={loading || !agreedToWaiver}
            className="w-full bg-[#FFB612] text-black hover:bg-[#E5A00D] font-bold uppercase tracking-wide h-12"
            data-testid="waiver-submit-btn"
          >
            {loading ? "Submitting..." : "Sign & Continue"}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>

        <p className="text-center text-white/40 text-sm mt-6">
          <a href="/dashboard" className="hover:text-[#FFB612]">← Back to Dashboard</a>
        </p>
      </div>
    </div>
  );
};
